// Admin instrument editor (Registrar only): edit every piece of instrument
// text inline (with the **bold** / *italic* / _underline_ subset), reorder and
// move questions between sections, attach follow-up questions to a scale item
// or a reflection question, add sections, and publish the result as a NEW
// immutable version. Published versions are used by newly created cycles;
// assessments already open or closed keep the version they started with.
// Display numbering is rewritten from position on every change — item ids
// underneath never change (analysis and the safeguarding settings key on them).

import { useEffect, useMemo, useRef, useState, type JSX, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  classifyInstrumentChange,
  diffInstrumentText,
  effectiveDesignations,
  hasDesignations,
  newInstrumentId,
  normalizeInstrument,
  validateInstrument,
  type InstrumentChange,
  type InstrumentDefinition,
  type InstrumentDesignation,
  type InstrumentItem,
  type InstrumentSection,
  type InstrumentTextChange,
} from '@cca/parishes-shared';
import { healthApi, HealthApiError, type InstrumentVersionsResponse } from '../api';
import { formatNzDate } from '../format';
import { partitionFollowUps } from '../markup';
import { SectionStep, type ScoreMap } from '../survey/SectionStep';
import { SurveyPreview } from '../SurveyPreview';
import { InlineMarkupEditor } from './InlineMarkupEditor';
import { CopyFromOverlay } from './CopyFromOverlay';
import { IngestOverlay } from './IngestOverlay';
import '../health.css';

const DRAFT_KEY = 'health-instrument-draft';

interface StoredDraft {
  baseVersion: string;
  /** Older drafts predate named instruments and may omit this. */
  baseFamilyId?: string;
  savedAt: string;
  definition: InstrumentDefinition;
}

function readStoredDraft(): StoredDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredDraft;
    if (!parsed?.definition?.sections || !parsed.savedAt) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Keep the reflection array in canonical "parent, then its follow-ups" order:
 *  scale-item follow-ups first (in scorecard order), then each top-level
 *  question followed by its own follow-ups. Anything orphaned stays at the end
 *  so it remains visible (the validator refuses to publish it anyway). */
function canonicalizeReflection(section: InstrumentSection): void {
  const { topReflection, followUps } = partitionFollowUps(section);
  const ordered: InstrumentItem[] = [];
  for (const item of section.scorecard) ordered.push(...(followUps.get(item.id) ?? []));
  for (const q of topReflection) ordered.push(q, ...(followUps.get(q.id) ?? []));
  const included = new Set(ordered.map((q) => q.id));
  for (const q of section.reflection) if (!included.has(q.id)) ordered.push(q);
  section.reflection = ordered;
}

function moveInArray<T>(arr: T[], from: number, to: number): void {
  if (to < 0 || to >= arr.length) return;
  const [item] = arr.splice(from, 1);
  if (item !== undefined) arr.splice(to, 0, item);
}

/** Carry the module-settings safeguarding/governance lists into a loaded
 *  version that predates in-instrument designations, so the next publish
 *  bakes them in. A fully-covered section gets the blanket designation;
 *  partially-covered sections get per-item marks. Returns null when the
 *  version already carries its own designations (nothing to do). */
function preMarkDesignations(
  def: InstrumentDefinition,
  tokens: { critical: string[]; governance: string[] },
): InstrumentDefinition | null {
  if (hasDesignations(def)) return null;
  const eff = effectiveDesignations(def, tokens);
  if (eff.criticalIds.length === 0 && eff.governanceIds.length === 0) return null;
  const critical = new Set(eff.criticalIds);
  const governance = new Set(eff.governanceIds);
  const next = structuredClone(def);
  for (const s of next.sections) {
    const inCritical = s.scorecard.filter((it) => critical.has(it.id)).length;
    const inGovernance = s.scorecard.filter((it) => governance.has(it.id)).length;
    if (s.scorecard.length > 0 && inCritical + inGovernance === s.scorecard.length) {
      // Every item is designated: blanket the majority on the section and
      // override the minority per item (the tidy shape for e.g. a
      // governance section whose first two items are safeguarding).
      const blanket: InstrumentDesignation =
        inCritical >= inGovernance ? 'safeguarding' : 'governance';
      const minority: InstrumentDesignation =
        blanket === 'safeguarding' ? 'governance' : 'safeguarding';
      const minoritySet = blanket === 'safeguarding' ? governance : critical;
      s.designation = blanket;
      for (const it of s.scorecard) if (minoritySet.has(it.id)) it.designation = minority;
    } else {
      for (const it of s.scorecard) {
        if (critical.has(it.id)) it.designation = 'safeguarding';
        else if (governance.has(it.id)) it.designation = 'governance';
      }
    }
  }
  return next;
}

/** Version-prefix slug from an instrument name ("Leadership Pulse" →
 *  "leadership-pulse"), capped at a word boundary. Mirrors the worker's
 *  versionSlug — keep in step. */
function versionSlug(name: string): string {
  const full = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (full.length <= 24) return full;
  const cut = full.slice(0, 24);
  const lastDash = cut.lastIndexOf('-');
  return (lastDash > 0 ? cut.slice(0, lastDash) : cut).replace(/-+$/g, '');
}

export function InstrumentEditorPage(): JSX.Element {
  const navigate = useNavigate();

  const [versions, setVersions] = useState<InstrumentVersionsResponse | null>(null);
  const [draft, setDraft] = useState<InstrumentDefinition | null>(null);
  const [baseVersion, setBaseVersion] = useState('');
  const [selectedFamilyId, setSelectedFamilyId] = useState('');
  const [dirty, setDirty] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [restorable, setRestorable] = useState<StoredDraft | null>(null);

  const [publishVersion, setPublishVersion] = useState('');
  const [makeDefault, setMakeDefault] = useState(true);
  const [publishing, setPublishing] = useState(false);
  // Publish target: the selected instrument, or a brand-new named one (the
  // "duplicate as a new instrument" flow).
  const [publishNew, setPublishNew] = useState(false);
  const [newFamilyName, setNewFamilyName] = useState('');
  // Once the number is hand-edited, name typing stops re-prefilling it.
  const [versionEdited, setVersionEdited] = useState(false);

  const [previewSection, setPreviewSection] = useState<string | null>(null);
  const [previewScores, setPreviewScores] = useState<ScoreMap>({});
  const [previewTexts, setPreviewTexts] = useState<Record<string, string>>({});
  const [previewScaleHelp, setPreviewScaleHelp] = useState(false);
  const [fullPreview, setFullPreview] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [ingestOpen, setIngestOpen] = useState(false);
  // In-place edit (correct wording / update this version) overlays.
  const [correctOpen, setCorrectOpen] = useState(false);
  const [reviseOpen, setReviseOpen] = useState(false);
  const [reviseAck, setReviseAck] = useState(false);
  const [editing, setEditing] = useState(false);
  // Collapsed-by-default sections keep a 14-section instrument scannable;
  // ids absent from this set are collapsed. Never part of the draft itself.
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const toggleSection = (id: string): void => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Module-settings safeguarding/governance tokens, used to carry designations
  // into a version that predates them.
  const settingsTokens = useRef<{ critical: string[]; governance: string[] }>({
    critical: [],
    governance: [],
  });
  // The exact stored definition of the loaded version — the baseline the
  // in-place edit gate classifies the draft against (kept RAW, never pre-marked,
  // so a plain typo fix reads as a wording change rather than a designation
  // change; carrying designations in is now an explicit action, below).
  const baseDefinitionRef = useRef<InstrumentDefinition | null>(null);
  // True when the loaded version has no designations but the settings do — the
  // "carry markings in" action is offered (and creates a new version).
  const [canCarryDesignations, setCanCarryDesignations] = useState(false);

  const draftTimer = useRef<number | null>(null);

  /** Load a stored definition into the draft AND record it as the correction
   *  baseline. Never mutates the draft on load — designations are carried in
   *  only when the registrar asks (a version-level change, so it routes to
   *  publishing a new version, not an in-place correction). */
  const adoptDefinition = (definition: InstrumentDefinition): void => {
    baseDefinitionRef.current = structuredClone(definition);
    setExpandedSections(new Set());
    setDraft(structuredClone(definition));
    setCanCarryDesignations(preMarkDesignations(definition, settingsTokens.current) !== null);
  };

  /** Explicitly carry safeguarding/governance markings in from the module
   *  settings (for a legacy un-designated version). This is a designation
   *  change, so it will be published as a new version, not corrected in place. */
  const carryDesignations = (): void => {
    if (!draft) return;
    const marked = preMarkDesignations(draft, settingsTokens.current);
    if (!marked) return;
    setDraft(structuredClone(marked));
    setDirty(true);
    setCanCarryDesignations(false);
    setNotice({
      kind: 'ok',
      text: 'Safeguarding and governance markings were carried in from the module settings. Review them on each section, then publish as a new version.',
    });
  };

  useEffect(() => {
    healthApi
      .instrumentVersions()
      .then(async (v) => {
        setVersions(v);
        setSelectedFamilyId(v.default_family_id);
        setPublishVersion(
          v.families.find((f) => f.id === v.default_family_id)?.suggested_next ??
            v.suggested_next_new_family,
        );
        // Settings first: the designation pre-mark needs the token lists.
        await healthApi
          .settings()
          .then(({ settings }) => {
            settingsTokens.current = {
              critical: settings.critical_items,
              governance: settings.governance_items,
            };
          })
          .catch(() => {
            /* pre-marking is best-effort; the editor works without it */
          });
        const current = await healthApi.instrument(v.default_version);
        adoptDefinition(current.definition);
        setBaseVersion(current.version);
        setRestorable(readStoredDraft());
      })
      .catch((err: unknown) => {
        if (err instanceof HealthApiError && err.status === 401) {
          navigate('/auth/login?returnTo=%2Fportal%2Fhealth%2Fadmin%2Finstrument', {
            replace: true,
          });
          return;
        }
        setLoadError(
          err instanceof HealthApiError && err.status === 403
            ? 'The instrument editor is only open to the Registrar.'
            : 'The instrument could not be loaded. Please try again.',
        );
      });
  }, [navigate]);

  // Local draft insurance: an instrument edit can be a long sitting.
  useEffect(() => {
    if (!draft || !dirty) return;
    if (draftTimer.current !== null) window.clearTimeout(draftTimer.current);
    draftTimer.current = window.setTimeout(() => {
      try {
        localStorage.setItem(
          DRAFT_KEY,
          JSON.stringify({
            baseVersion,
            baseFamilyId: selectedFamilyId,
            savedAt: new Date().toISOString(),
            definition: draft,
          } satisfies StoredDraft),
        );
      } catch {
        /* storage full/blocked: the draft simply isn't insured */
      }
    }, 500);
    return () => {
      if (draftTimer.current !== null) window.clearTimeout(draftTimer.current);
    };
  }, [draft, dirty, baseVersion, selectedFamilyId]);

  // Blocking validation only. The safeguarding/governance item ALERTS were
  // removed at Steve's call (2026-07-05) — the per-question tier model is
  // parked for scoping and the id-list warnings carried no weight meanwhile.
  const validation = useMemo(() => {
    if (!draft) return { errors: [] as string[] };
    const normalized = normalizeInstrument({
      ...draft,
      version: publishVersion || draft.version,
    });
    return { errors: validateInstrument(normalized).errors };
  }, [draft, publishVersion]);

  // How the draft differs from the loaded version, for the graduated in-place
  // edit: 'wording' corrects freely, 'additive' behind a firm warning,
  // 'destructive' needs a new version (baseVersion recomputes when a new one
  // is loaded, which also replaces the draft).
  const change: InstrumentChange = useMemo(() => {
    if (!draft || !baseDefinitionRef.current) return { kind: 'none', reasons: [] };
    return classifyInstrumentChange(baseDefinitionRef.current, draft);
  }, [draft, baseVersion]);
  const textChanges: InstrumentTextChange[] = useMemo(() => {
    if (!draft || !baseDefinitionRef.current) return [];
    return diffInstrumentText(baseDefinitionRef.current, draft);
  }, [draft, baseVersion]);

  if (loadError) {
    return (
      <div className="card" style={{ maxWidth: 560, margin: '30px auto', textAlign: 'center' }}>
        <p style={{ fontSize: 14, color: 'var(--health-grey)', lineHeight: 1.7 }}>{loadError}</p>
      </div>
    );
  }
  if (!draft || !versions) {
    return <p style={{ color: 'var(--health-grey)' }}>Loading the instrument...</p>;
  }

  /** Every mutation funnels through here: clone, mutate, renumber, mark dirty. */
  const update = (fn: (d: InstrumentDefinition) => void): void => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      fn(next);
      next.sections.forEach((s, i) => {
        s.no = i + 1;
      });
      return next;
    });
    setDirty(true);
    setNotice(null);
  };

  const selectedFamily = versions.families.find((f) => f.id === selectedFamilyId) ?? null;

  /** Self-describing suggestion for a NEW instrument: `<name-slug>-YYYY.minor`,
   *  numbered past everything already published anywhere. */
  const suggestForNewName = (name: string): string => {
    const slug = versionSlug(name);
    const fallback = versions.suggested_next_new_family;
    if (!slug) return fallback;
    const year = /^(\d{4})\./.exec(fallback)?.[1] ?? String(new Date().getFullYear());
    const all = new Set(versions.families.flatMap((f) => f.versions.map((v) => v.version)));
    let minor = 1;
    while (all.has(`${slug}-${year}.${minor}`)) minor++;
    return `${slug}-${year}.${minor}`;
  };

  const loadVersion = (version: string): void => {
    if (
      dirty &&
      !window.confirm('Discard the current unpublished changes and load ' + version + '?')
    ) {
      return;
    }
    healthApi
      .instrument(version)
      .then((res) => {
        adoptDefinition(res.definition);
        setBaseVersion(res.version);
        setSelectedFamilyId(res.family_id);
        setDirty(false);
        localStorage.removeItem(DRAFT_KEY);
      })
      .catch(() => setNotice({ kind: 'err', text: 'That version could not be loaded.' }));
  };

  /** Switch instruments: load the chosen one's default-or-latest version. */
  const selectFamily = (famId: string): void => {
    if (famId === selectedFamilyId || !versions) return;
    const fam = versions.families.find((f) => f.id === famId);
    if (!fam || fam.versions.length === 0) return;
    if (
      dirty &&
      !window.confirm(`Discard the current unpublished changes and switch to "${fam.name}"?`)
    ) {
      return;
    }
    const base =
      fam.versions.find((v) => v.is_default)?.version ??
      fam.versions[fam.versions.length - 1]?.version;
    if (!base) return;
    healthApi
      .instrument(base)
      .then((res) => {
        setSelectedFamilyId(famId);
        setBaseVersion(res.version);
        setDirty(false);
        localStorage.removeItem(DRAFT_KEY);
        setPublishNew(false);
        setNewFamilyName('');
        setVersionEdited(false);
        setPublishVersion(fam.suggested_next);
        setNotice(null);
        adoptDefinition(res.definition);
      })
      .catch(() => setNotice({ kind: 'err', text: 'That instrument could not be loaded.' }));
  };

  /** Rename the catalogue label; published versions never change. */
  const renameFamily = (): void => {
    if (!selectedFamily) return;
    const name = window.prompt(
      'Rename this instrument. This changes the catalogue name only — published versions, and the title respondents saw in them, never change.',
      selectedFamily.name,
    );
    if (name === null) return;
    healthApi
      .renameInstrumentFamily(selectedFamily.id, name.trim())
      .then(async (res) => {
        setVersions(await healthApi.instrumentVersions());
        setNotice({ kind: 'ok', text: `Renamed to "${res.name}".` });
      })
      .catch((err: unknown) => {
        const code = err instanceof HealthApiError ? err.code : null;
        setNotice({
          kind: 'err',
          text:
            code === 'family_name_exists'
              ? 'Another instrument already has that name.'
              : code === 'invalid_family_name'
                ? 'That name cannot be used — give it 1 to 120 characters.'
                : 'Renaming failed. Please try again.',
        });
      });
  };

  const restoreDraft = (): void => {
    if (!restorable) return;
    setExpandedSections(new Set());
    setDraft(structuredClone(restorable.definition));
    setBaseVersion(restorable.baseVersion);
    // Recover the correction baseline for the restored draft's base version so
    // the in-place edit gate still works (best-effort; correction is just
    // unavailable if it can't be fetched).
    baseDefinitionRef.current = null;
    setCanCarryDesignations(false);
    healthApi
      .instrument(restorable.baseVersion)
      .then((res) => {
        baseDefinitionRef.current = structuredClone(res.definition);
      })
      .catch(() => {
        /* correction gate stays closed; publish still works */
      });
    if (
      restorable.baseFamilyId &&
      versions.families.some((f) => f.id === restorable.baseFamilyId)
    ) {
      setSelectedFamilyId(restorable.baseFamilyId);
      setPublishVersion(
        versions.families.find((f) => f.id === restorable.baseFamilyId)?.suggested_next ??
          publishVersion,
      );
    }
    setDirty(true);
    setRestorable(null);
  };
  const discardDraft = (): void => {
    localStorage.removeItem(DRAFT_KEY);
    setRestorable(null);
  };

  /** Commit an in-place edit (wording or additive) to the loaded version. */
  const commitEdit = (): void => {
    if (!draft) return;
    setEditing(true);
    healthApi
      .editInstrumentVersion(baseVersion, normalizeInstrument(draft))
      .then(async (res) => {
        setCorrectOpen(false);
        setReviseOpen(false);
        setReviseAck(false);
        localStorage.removeItem(DRAFT_KEY);
        const reloaded = await healthApi.instrument(baseVersion);
        adoptDefinition(reloaded.definition);
        setDirty(false);
        setVersions(await healthApi.instrumentVersions());
        const n = res.affected_cycles ?? 0;
        setNotice({
          kind: 'ok',
          text:
            res.change_kind === 'additive'
              ? `${baseVersion} updated${n > 0 ? ` — ${n} assessment${n === 1 ? '' : 's'} refresh their results on next view` : ''}.`
              : `Wording corrected on ${baseVersion}.`,
        });
        window.scrollTo({ top: 0 });
      })
      .catch((err: unknown) => {
        const code = err instanceof HealthApiError ? err.code : null;
        setCorrectOpen(false);
        setReviseOpen(false);
        setNotice({
          kind: 'err',
          text:
            code === 'destructive_change'
              ? 'That change removes or reorders questions, so it needs a new version instead.'
              : 'The edit could not be saved. Please try again.',
        });
      })
      .finally(() => setEditing(false));
  };

  const publish = (): void => {
    const version = publishVersion.trim();
    if (!version) {
      setNotice({ kind: 'err', text: 'Give the new version a number first.' });
      return;
    }
    const targetName = publishNew ? newFamilyName.trim() : (selectedFamily?.name ?? '');
    if (publishNew && !targetName) {
      setNotice({ kind: 'err', text: 'Give the new instrument a name first.' });
      return;
    }
    if (!publishNew && !selectedFamily) {
      setNotice({ kind: 'err', text: 'Choose an instrument to publish into.' });
      return;
    }
    if (
      !window.confirm(
        `Publish version ${version} of "${targetName}"? Published versions cannot be changed; new assessment cycles will use it${makeDefault ? '' : ' only when chosen for the cycle'}.`,
      )
    ) {
      return;
    }
    setPublishing(true);
    setNotice(null);
    healthApi
      .publishInstrument(
        normalizeInstrument({ ...draft, version }),
        makeDefault,
        publishNew ? { newFamilyName: targetName } : { familyId: selectedFamilyId },
      )
      .then(async (res) => {
        localStorage.removeItem(DRAFT_KEY);
        setDirty(false);
        setBaseVersion(res.version);
        const v = await healthApi.instrumentVersions();
        setVersions(v);
        setSelectedFamilyId(res.family_id);
        setPublishNew(false);
        setNewFamilyName('');
        setVersionEdited(false);
        setPublishVersion(
          v.families.find((f) => f.id === res.family_id)?.suggested_next ??
            v.suggested_next_new_family,
        );
        setNotice({
          kind: 'ok',
          text:
            `Version ${res.version} of "${res.family_name}" published${res.is_default ? ' and set as the version new cycles use' : ''}.` +
            '',
        });
        window.scrollTo({ top: 0 });
      })
      .catch((err: unknown) => {
        const code = err instanceof HealthApiError ? err.code : null;
        if (code === 'version_exists') {
          setNotice({
            kind: 'err',
            text: `Version ${version} already exists (version numbers are shared across instruments). Pick a new number.`,
          });
        } else if (code === 'family_name_exists') {
          setNotice({ kind: 'err', text: 'Another instrument already has that name.' });
        } else if (code === 'invalid_family_name') {
          setNotice({
            kind: 'err',
            text: 'That instrument name cannot be used — give it 1 to 120 characters.',
          });
        } else if (code === 'invalid_definition') {
          const detail =
            (err instanceof HealthApiError ? (err.body as { detail?: string[] }) : {})?.detail ??
            [];
          setNotice({ kind: 'err', text: `The instrument was not accepted: ${detail.join(' ')}` });
        } else {
          setNotice({ kind: 'err', text: 'Publishing failed. Please try again.' });
        }
      })
      .finally(() => setPublishing(false));
  };

  const scaleLabels = ['1', '2', '3', '4'];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 16, maxWidth: 900 }}>
      <div>
        <h1 style={{ fontSize: 22, marginBottom: 4 }}>Instrument editor</h1>
        <p style={{ fontSize: 13.5, color: 'var(--health-grey)', lineHeight: 1.7 }}>
          Edit the assessment's wording, add or move questions, and publish the result as a new
          version. Publishing never changes an assessment that is already underway: open and closed
          cycles keep the version they started with. Select text to make it <strong>bold</strong>,{' '}
          <em>italic</em>, or <u>underlined</u>.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          <button
            type="button"
            className="btn btn-sm"
            data-tip="Walk through the whole survey as respondents will see it, including your unpublished changes"
            onClick={() => setFullPreview(true)}
          >
            Preview the whole survey
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            data-tip="Bring sections or questions across from another instrument's published version"
            onClick={() => setCopyOpen(true)}
          >
            Copy from another instrument
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            data-tip="Paste a proposed instrument document and have it ingested into a draft you can review and publish"
            onClick={() => setIngestOpen(true)}
          >
            Ingest a document
          </button>
        </div>
      </div>

      {restorable && (
        <div className="health-banner" role="status">
          An unpublished draft from {formatNzDate(restorable.savedAt)} (based on{' '}
          {restorable.baseVersion}) was saved on this device.{' '}
          <button
            type="button"
            className="btn btn-sm"
            data-tip="Load the saved draft and carry on editing it"
            onClick={restoreDraft}
          >
            Restore it
          </button>{' '}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            data-tip="Delete the saved draft from this device (published versions are unaffected)"
            onClick={discardDraft}
          >
            Discard
          </button>
        </div>
      )}

      {notice && (
        <div
          className={`health-banner ${notice.kind === 'err' ? 'health-banner-red' : 'health-banner-blue'}`}
          role={notice.kind === 'err' ? 'alert' : 'status'}
        >
          {notice.text}
        </div>
      )}

      {validation.errors.length > 0 && (
        <div className="health-banner health-banner-red" role="alert">
          <strong>Before this can be published:</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {validation.errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="card instr-toolbar">
        <span className="instr-toolbar-group">
          <span className="instr-toolbar-label">Instrument</span>
          <select
            className="instr-field instr-field-wide"
            value={selectedFamilyId}
            onChange={(e) => selectFamily(e.target.value)}
            aria-label="Instrument to work on"
            title="Each instrument has its own name and version history — switch here to work on another one"
          >
            {versions.families.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name} ({f.versions.length} {f.versions.length === 1 ? 'version' : 'versions'})
              </option>
            ))}
          </select>
          <button
            type="button"
            className="instr-ctl instr-ctl-accent"
            data-tip="Rename this instrument — catalogue label only; published versions never change"
            onClick={renameFamily}
          >
            Rename
          </button>
        </span>
        <span className="instr-toolbar-group">
          <span className="instr-toolbar-label">Editing from</span>
          <select
            className="instr-field"
            value={baseVersion}
            onChange={(e) => loadVersion(e.target.value)}
            aria-label="Version to edit from"
            title="Choose which published version to start editing from — your changes become a new version, this one is never altered"
          >
            {(selectedFamily?.versions ?? []).map((v) => (
              <option key={v.version} value={v.version}>
                {v.version}
                {v.is_default ? ' (current default)' : ''}
              </option>
            ))}
          </select>
        </span>
      </div>

      <div className="card">
        <h2 className="health-section-heading">
          <span>Basis</span>
        </h2>
        <div style={{ display: 'grid', gap: 4 }}>
          <span className="health-input-label">Instrument title</span>
          <InlineMarkupEditor
            value={draft.title}
            onSave={(v) => update((d) => (d.title = v))}
            placeholder="Instrument title"
          />
        </div>
        <div style={{ marginTop: 14, display: 'grid', gap: 4 }}>
          <span className="health-input-label">Scale wording (1 to 4)</span>
          {scaleLabels.map((n) => (
            <div key={n} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <strong style={{ width: 16, textAlign: 'center' }}>{n}</strong>
              <div style={{ flex: 1 }}>
                <InlineMarkupEditor
                  value={draft.scale.labels[n] ?? ''}
                  onSave={(v) => update((d) => (d.scale.labels[n] = v))}
                  placeholder={`What a score of ${n} means`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button
          type="button"
          className="instr-ctl"
          onClick={() => setExpandedSections(new Set(draft.sections.map((s) => s.id)))}
        >
          Expand all
        </button>
        <button type="button" className="instr-ctl" onClick={() => setExpandedSections(new Set())}>
          Collapse all
        </button>
      </div>

      {draft.sections.map((section, si) => (
        <SectionEditorCard
          key={section.id}
          section={section}
          sectionIndex={si}
          sectionCount={draft.sections.length}
          allSections={draft.sections}
          update={update}
          previewOpen={previewSection === section.id}
          onTogglePreview={() =>
            setPreviewSection((cur) => (cur === section.id ? null : section.id))
          }
          collapsed={!expandedSections.has(section.id)}
          onToggleCollapsed={() => toggleSection(section.id)}
        />
      ))}

      {previewSection !== null &&
        (() => {
          const section = draft.sections.find((s) => s.id === previewSection);
          if (!section) return null;
          return (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- backdrop click duplicates the explicit Close preview button inside the dialog
            <div
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(38, 34, 27, 0.45)',
                zIndex: 50,
                overflowY: 'auto',
                padding: '40px 16px',
              }}
              onClick={() => setPreviewSection(null)}
            >
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- click handler only stops the backdrop-dismiss propagating; not an interaction */}
              <div style={{ maxWidth: 760, margin: '0 auto' }} onClick={(e) => e.stopPropagation()}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}
                >
                  <span style={{ color: '#fff', fontSize: 13 }}>
                    Respondents will see this section exactly as below.
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => setPreviewSection(null)}
                  >
                    Close preview
                  </button>
                </div>
                <SectionStep
                  section={section}
                  scale={draft.scale.labels}
                  scores={previewScores}
                  texts={previewTexts}
                  onScore={(id, v) => setPreviewScores((p) => ({ ...p, [id]: v ?? null }))}
                  onText={(id, v) => setPreviewTexts((p) => ({ ...p, [id]: v }))}
                  scaleHelpOpen={previewScaleHelp}
                  onScaleHelpToggle={setPreviewScaleHelp}
                />
              </div>
            </div>
          );
        })()}

      {copyOpen && (
        <CopyFromOverlay
          versions={versions}
          draftSections={draft.sections}
          onClose={() => setCopyOpen(false)}
          onCopy={(payload) => {
            setExpandedSections((prev) => {
              const next = new Set(prev);
              for (const s of payload.newSections) next.add(s.id);
              return next;
            });
            update((d) => {
              d.sections.push(...payload.newSections);
              if (payload.intoSectionId && payload.items) {
                const target = d.sections.find((s) => s.id === payload.intoSectionId);
                if (target) {
                  target.scorecard.push(...payload.items.scorecard);
                  target.reflection.push(...payload.items.reflection);
                  canonicalizeReflection(target);
                }
              }
            });
            setCopyOpen(false);
            const counts = [
              payload.newSections.length > 0
                ? `${payload.newSections.length} section${payload.newSections.length === 1 ? '' : 's'}`
                : null,
              payload.copiedItemCount > 0
                ? `${payload.copiedItemCount} question${payload.copiedItemCount === 1 ? '' : 's'}`
                : null,
            ]
              .filter(Boolean)
              .join(' and ');
            setNotice({ kind: 'ok', text: `Copied ${counts} into the draft.` });
          }}
        />
      )}

      {ingestOpen && (
        <IngestOverlay
          onClose={() => setIngestOpen(false)}
          onIngested={(definition) => {
            if (
              dirty &&
              !window.confirm(
                'Replace the current unpublished changes with the document just ingested?',
              )
            ) {
              return;
            }
            setIngestOpen(false);
            setExpandedSections(new Set());
            // A read-in document is a new instrument, not an edit of a stored
            // version — no correction baseline.
            baseDefinitionRef.current = null;
            setCanCarryDesignations(false);
            setDraft(structuredClone(definition));
            setDirty(true);
            // A read-in document is almost always a new instrument: preselect
            // the duplicate flow with its title as the suggested name.
            setPublishNew(true);
            const suggested = definition.title.replace(/\*|_/g, '').trim();
            setNewFamilyName(suggested);
            setVersionEdited(false);
            setPublishVersion(
              suggested ? suggestForNewName(suggested) : versions.suggested_next_new_family,
            );
            setNotice({
              kind: 'ok',
              text: 'The document was ingested into the draft below. Check every section and question against the original — it can misread or miss items — then publish.',
            });
            window.scrollTo({ top: 0 });
          }}
        />
      )}

      {correctOpen &&
        (() => {
          const affected =
            selectedFamily?.versions.find((v) => v.version === baseVersion)?.cycle_count ?? 0;
          return (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- backdrop click duplicates the explicit Cancel button inside the dialog
            <div
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(38, 34, 27, 0.45)',
                zIndex: 50,
                overflowY: 'auto',
                padding: '40px 16px',
              }}
              onClick={() => !editing && setCorrectOpen(false)}
            >
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- click handler only stops the backdrop-dismiss propagating; not an interaction */}
              <div
                className="card"
                style={{ maxWidth: 640, margin: '0 auto' }}
                onClick={(e) => e.stopPropagation()}
              >
                <h2 className="health-section-heading">
                  <span>Correct wording on {baseVersion}</span>
                </h2>
                <p style={{ fontSize: 13, lineHeight: 1.7, marginBottom: 10 }}>
                  {textChanges.length} wording change{textChanges.length === 1 ? '' : 's'} will be
                  saved onto {baseVersion} in place.
                  {affected > 0
                    ? ` It affects ${affected} assessment${affected === 1 ? '' : 's'} already using it, including any that have closed.`
                    : ''}{' '}
                  The questions, structure, and version number do not change.
                </p>
                <div
                  style={{
                    display: 'grid',
                    gap: 8,
                    maxHeight: 320,
                    overflowY: 'auto',
                    marginBottom: 14,
                  }}
                >
                  {textChanges.map((tc) => (
                    <div
                      key={tc.path}
                      style={{
                        fontSize: 12.5,
                        borderBottom: '1px solid var(--health-border-soft)',
                        paddingBottom: 6,
                      }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>{tc.label}</div>
                      <div style={{ color: 'var(--band-priority)' }}>
                        − {tc.before || '(empty)'}
                      </div>
                      <div style={{ color: 'var(--health-accent-dk)' }}>
                        + {tc.after || '(empty)'}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={editing}
                    onClick={() => setCorrectOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={editing}
                    onClick={commitEdit}
                  >
                    {editing ? 'Saving...' : 'Correct wording'}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {reviseOpen &&
        (() => {
          const affected =
            selectedFamily?.versions.find((v) => v.version === baseVersion)?.cycle_count ?? 0;
          return (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- backdrop click duplicates the explicit Cancel button inside the dialog
            <div
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(38, 34, 27, 0.45)',
                zIndex: 50,
                overflowY: 'auto',
                padding: '40px 16px',
              }}
              onClick={() => !editing && setReviseOpen(false)}
            >
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- click handler only stops the backdrop-dismiss propagating; not an interaction */}
              <div
                className="card"
                style={{
                  maxWidth: 640,
                  margin: '0 auto',
                  borderLeft: '4px solid var(--band-priority)',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <h2 className="health-section-heading">
                  <span>Update {baseVersion} in place</span>
                </h2>
                <p style={{ fontSize: 13.5, lineHeight: 1.7, marginBottom: 10 }}>
                  You are changing an instrument that is <strong>already in use</strong>
                  {affected > 0
                    ? ` by ${affected} assessment${affected === 1 ? '' : 's'}, including any that have closed`
                    : ''}
                  . This adds questions to {baseVersion} without a new version.
                </p>
                <ul
                  style={{
                    fontSize: 13,
                    lineHeight: 1.7,
                    color: 'var(--health-grey)',
                    margin: '0 0 12px',
                    paddingLeft: 18,
                  }}
                >
                  <li>Answers already collected are kept and stay correct.</li>
                  <li>New questions show as unanswered for anyone who has already responded.</li>
                  <li>
                    Results summaries refresh on next view (and may be unavailable for older
                    assessments whose written answers have been purged).
                  </li>
                </ul>
                <label
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'flex-start',
                    fontSize: 13,
                    marginBottom: 14,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={reviseAck}
                    onChange={(e) => setReviseAck(e.target.checked)}
                  />
                  <span>
                    I understand this changes an instrument already in use, and want to update{' '}
                    {baseVersion} in place rather than publish a new version.
                  </span>
                </label>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={editing}
                    onClick={() => setReviseOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={editing || !reviseAck}
                    onClick={commitEdit}
                  >
                    {editing ? 'Saving...' : `Update ${baseVersion}`}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {fullPreview && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- backdrop click duplicates the explicit Close preview button inside the dialog
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(38, 34, 27, 0.45)',
            zIndex: 50,
            overflowY: 'auto',
            padding: '40px 16px',
          }}
          onClick={() => setFullPreview(false)}
        >
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- click handler only stops the backdrop-dismiss propagating; not an interaction */}
          <div style={{ maxWidth: 760, margin: '0 auto' }} onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
              }}
            >
              <span style={{ color: '#fff', fontSize: 13 }}>
                The whole survey, including your unpublished changes.
              </span>
              <button type="button" className="btn btn-sm" onClick={() => setFullPreview(false)}>
                Close preview
              </button>
            </div>
            <SurveyPreview
              definition={normalizeInstrument({
                ...draft,
                version: publishVersion || draft.version,
              })}
              context="unpublished draft"
            />
          </div>
        </div>
      )}

      <div>
        <button
          type="button"
          className="btn btn-ghost"
          data-tip="Add a new empty section at the end — it stays hidden from respondents until it has questions"
          onClick={() => {
            const id = newInstrumentId('sec');
            update((d) => {
              d.sections.push({
                no: d.sections.length + 1,
                id,
                title: '',
                aim: '',
                scorecard: [],
                reflection: [],
              });
            });
            setExpandedSections((prev) => new Set(prev).add(id));
          }}
        >
          + Add a section
        </button>
      </div>

      {baseVersion && change.kind !== 'none' && validation.errors.length === 0 && (
        <div className="card">
          <h2 className="health-section-heading">
            <span>Edit {baseVersion} in place</span>
          </h2>
          {change.kind === 'wording' && (
            <>
              <p style={{ fontSize: 13, lineHeight: 1.7, marginBottom: 12 }}>
                This is a <strong>wording change</strong> to {baseVersion}. You can correct it in
                place — the questions, structure, and version number do not change — or publish it
                as a new version below.
              </p>
              <button
                type="button"
                className="btn btn-primary"
                disabled={editing}
                onClick={() => setCorrectOpen(true)}
              >
                Correct wording on {baseVersion}
              </button>
            </>
          )}
          {change.kind === 'additive' && (
            <>
              <p style={{ fontSize: 13, lineHeight: 1.7, marginBottom: 12 }}>
                This <strong>adds to</strong> {baseVersion}. Existing answers are kept; new
                questions simply show as unanswered for anyone who has already responded. Because it
                changes a version already in use, it asks you to confirm.
              </p>
              <button
                type="button"
                className="btn btn-primary"
                disabled={editing}
                onClick={() => {
                  setReviseAck(false);
                  setReviseOpen(true);
                }}
              >
                Update this version
              </button>
            </>
          )}
          {change.kind === 'destructive' && (
            <div className="health-banner health-banner-red" role="status">
              <strong>This change can’t be made to {baseVersion} in place:</strong>
              <ul style={{ margin: '6px 0 8px', paddingLeft: 18 }}>
                {change.reasons.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
              Removing, reordering, or re-identifying questions can misalign answers already
              collected, so publish this as a new version below instead.
            </div>
          )}
        </div>
      )}

      {baseVersion && change.kind === 'none' && canCarryDesignations && (
        <div className="health-banner health-banner-blue" role="status">
          {baseVersion} has no safeguarding or governance markings yet.{' '}
          <button type="button" className="btn btn-sm" onClick={carryDesignations}>
            Carry them in from the module settings
          </button>{' '}
          — reviewed and published as a new version.
        </div>
      )}

      <div className="card">
        <h2 className="health-section-heading">
          <span>Publish as a new version</span>
        </h2>
        <p style={{ fontSize: 13, color: 'var(--health-grey)', lineHeight: 1.7, marginBottom: 12 }}>
          Publishing creates a new, unchangeable version. Assessments already open or closed keep
          the version they started with; an assessment's instrument can also be chosen on its own
          Cycle settings tab before anyone responds.
        </p>
        <div
          style={{
            display: 'flex',
            gap: 16,
            alignItems: 'center',
            flexWrap: 'wrap',
            marginBottom: 12,
          }}
        >
          <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontSize: 13.5 }}>
            <input
              type="radio"
              name="publish-target"
              checked={!publishNew}
              onChange={() => {
                setPublishNew(false);
                setVersionEdited(false);
                setPublishVersion(selectedFamily?.suggested_next ?? publishVersion);
              }}
            />
            Into <strong>{selectedFamily?.name ?? 'this instrument'}</strong>
          </label>
          <label
            style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontSize: 13.5 }}
            title="Duplicate: publishes your current draft as the first version of a brand-new instrument with its own name and history"
          >
            <input
              type="radio"
              name="publish-target"
              checked={publishNew}
              onChange={() => {
                setPublishNew(true);
                setVersionEdited(false);
                setPublishVersion(
                  newFamilyName
                    ? suggestForNewName(newFamilyName)
                    : versions.suggested_next_new_family,
                );
              }}
            />
            As a new instrument named
          </label>
          <input
            className="instr-field"
            placeholder="e.g. Leadership Pulse"
            aria-label="New instrument name"
            value={newFamilyName}
            onChange={(e) => {
              setNewFamilyName(e.target.value);
              // Live self-describing prefill (leadership-pulse-2026.1) unless
              // the number has been hand-edited.
              if (!versionEdited) {
                setPublishVersion(
                  e.target.value
                    ? suggestForNewName(e.target.value)
                    : versions.suggested_next_new_family,
                );
              }
            }}
            onFocus={() => {
              if (!publishNew) {
                setPublishNew(true);
                setVersionEdited(false);
                setPublishVersion(
                  newFamilyName
                    ? suggestForNewName(newFamilyName)
                    : versions.suggested_next_new_family,
                );
              }
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontSize: 13.5 }}>
            Version number
            <input
              className="instr-field"
              value={publishVersion}
              onChange={(e) => {
                setPublishVersion(e.target.value);
                setVersionEdited(true);
              }}
              aria-label="New version number"
              title="The number this will be published under — each number can only ever be published once. New instruments suggest a self-describing form like leadership-pulse-2026.1"
            />
          </label>
          <label
            style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontSize: 13.5 }}
            title="Ticked: assessments created from now on use this version. Assessments already open or closed are never affected either way."
          >
            <input
              type="checkbox"
              checked={makeDefault}
              onChange={(e) => setMakeDefault(e.target.checked)}
            />
            Use this version for newly created cycles
          </label>
          <button
            type="button"
            className="btn btn-primary"
            data-tip="Creates a permanent version that can never be edited — to change it later, publish a further version"
            disabled={publishing || validation.errors.length > 0}
            onClick={publish}
          >
            {publishing ? 'Publishing...' : `Publish ${publishVersion || 'new version'}`}
          </button>
        </div>
      </div>

      <div className="card">
        <h2 className="health-section-heading">
          <span>Published versions — {selectedFamily?.name ?? 'instrument'}</span>
        </h2>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Version</th>
                <th>Published</th>
                <th>Used by</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(selectedFamily?.versions ?? []).map((v) => (
                <tr key={v.version}>
                  <td>{v.version}</td>
                  <td>{formatNzDate(v.published_at)}</td>
                  <td>
                    {v.cycle_count} {v.cycle_count === 1 ? 'cycle' : 'cycles'}
                  </td>
                  <td>
                    {v.is_default && (
                      <span className="pill pill-success">Default for new cycles</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── One section's editing card ────────────────────────────────────────────────

function SectionEditorCard({
  section,
  sectionIndex,
  sectionCount,
  allSections,
  update,
  previewOpen,
  onTogglePreview,
  collapsed,
  onToggleCollapsed,
}: {
  section: InstrumentSection;
  sectionIndex: number;
  sectionCount: number;
  allSections: InstrumentSection[];
  update: (fn: (d: InstrumentDefinition) => void) => void;
  previewOpen: boolean;
  onTogglePreview: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}): JSX.Element {
  const { topReflection, followUps } = partitionFollowUps(section);

  const withSection = (fn: (s: InstrumentSection) => void) =>
    update((d) => {
      const s = d.sections[sectionIndex];
      if (!s) return;
      fn(s);
      canonicalizeReflection(s);
    });

  const deleteSection = (): void => {
    const count = section.scorecard.length + section.reflection.length;
    if (
      !window.confirm(
        count > 0
          ? `Delete the section "${section.title || 'Untitled'}" and its ${count} question${count === 1 ? '' : 's'}?`
          : `Delete the empty section "${section.title || 'Untitled'}"?`,
      )
    ) {
      return;
    }
    update((d) => {
      d.sections.splice(sectionIndex, 1);
    });
  };

  const deleteItem = (item: InstrumentItem): void => {
    const children = followUps.get(item.id) ?? [];
    const message =
      children.length > 0
        ? `Delete this question and its ${children.length} follow-up${children.length === 1 ? '' : 's'}?`
        : 'Delete this question?';
    if (!window.confirm(message)) return;
    withSection((s) => {
      s.scorecard = s.scorecard.filter((it) => it.id !== item.id);
      s.reflection = s.reflection.filter((q) => q.id !== item.id && q.parentId !== item.id);
    });
  };

  /** Move a top-level item (and its follow-ups) to another section. */
  const moveToSection = (
    item: InstrumentItem,
    kind: 'scorecard' | 'reflection',
    targetIdx: number,
  ): void => {
    update((d) => {
      const from = d.sections[sectionIndex];
      const to = d.sections[targetIdx];
      if (!from || !to) return;
      const children = from.reflection.filter((q) => q.parentId === item.id);
      from.scorecard = from.scorecard.filter((it) => it.id !== item.id);
      from.reflection = from.reflection.filter((q) => q.id !== item.id && q.parentId !== item.id);
      if (kind === 'scorecard') to.scorecard.push(item);
      else to.reflection.push(item);
      to.reflection.push(...children);
      canonicalizeReflection(from);
      canonicalizeReflection(to);
    });
  };

  /** Reorder among visible siblings (top-level within a list, or follow-ups
   *  under one parent). */
  const nudge = (
    item: InstrumentItem,
    kind: 'scorecard' | 'top' | 'followup',
    dir: -1 | 1,
  ): void => {
    withSection((s) => {
      if (kind === 'scorecard') {
        const idx = s.scorecard.findIndex((it) => it.id === item.id);
        moveInArray(s.scorecard, idx, idx + dir);
        return;
      }
      const view = partitionFollowUps(s);
      if (kind === 'top') {
        const tops = view.topReflection;
        const idx = tops.findIndex((q) => q.id === item.id);
        moveInArray(tops, idx, idx + dir);
        s.reflection = [...s.reflection.filter((q) => q.parentId), ...tops];
      } else {
        const siblings = view.followUps.get(item.parentId ?? '') ?? [];
        const idx = siblings.findIndex((q) => q.id === item.id);
        moveInArray(siblings, idx, idx + dir);
        s.reflection = [...s.reflection.filter((q) => q.parentId !== item.parentId), ...siblings];
      }
    });
  };

  const moveTargets = allSections.map((s, i) => ({ s, i })).filter(({ i }) => i !== sectionIndex);

  const itemControls = (
    item: InstrumentItem,
    kind: 'scorecard' | 'top' | 'followup',
    position: number,
    total: number,
  ): ReactNode => (
    <span className="instr-item-controls">
      <button
        type="button"
        className="instr-ctl"
        aria-label="Move up"
        data-tip="Move this question up one place"
        disabled={position === 0}
        onClick={() => nudge(item, kind, -1)}
      >
        ↑
      </button>
      <button
        type="button"
        className="instr-ctl"
        aria-label="Move down"
        data-tip="Move this question down one place"
        disabled={position === total - 1}
        onClick={() => nudge(item, kind, 1)}
      >
        ↓
      </button>
      {kind !== 'followup' && moveTargets.length > 0 && (
        <select
          className="instr-ctl"
          aria-label="Move to another section"
          title="Move this question — and any follow-ups it has — into another section"
          value=""
          onChange={(e) => {
            const idx = Number(e.target.value);
            if (Number.isFinite(idx) && e.target.value !== '') {
              moveToSection(item, kind === 'scorecard' ? 'scorecard' : 'reflection', idx);
            }
          }}
        >
          <option value="">Move to...</option>
          {moveTargets.map(({ s, i }) => (
            <option key={s.id} value={i}>
              {s.no}. {s.title || 'Untitled'}
            </option>
          ))}
        </select>
      )}
      {kind !== 'followup' && (
        <button
          type="button"
          className="instr-ctl"
          data-tip="Add a follow-up question — shown indented beneath this one, with its own written answer"
          onClick={() =>
            withSection((s) => {
              s.reflection.push({ id: newInstrumentId('q'), text: '', parentId: item.id });
            })
          }
        >
          + Follow-up
        </button>
      )}
      <button
        type="button"
        className="instr-ctl instr-ctl-danger"
        aria-label="Delete question"
        data-tip="Delete this question (any follow-ups go with it)"
        onClick={() => deleteItem(item)}
      >
        ✕
      </button>
    </span>
  );

  const followUpRows = (parent: InstrumentItem): ReactNode => {
    const children = followUps.get(parent.id) ?? [];
    return children.map((fu, fi) => (
      <div key={fu.id} className="instr-followup">
        <div className="instr-item-row" style={{ borderBottom: 0, padding: '2px 0' }}>
          <div className="instr-item-main">
            <InlineMarkupEditor
              value={fu.text}
              onSave={(v) =>
                withSection((s) => {
                  const q = s.reflection.find((r) => r.id === fu.id);
                  if (q) q.text = v;
                })
              }
              placeholder="Write the follow-up question..."
            />
          </div>
          {itemControls(fu, 'followup', fi, children.length)}
        </div>
      </div>
    ));
  };

  const groupInput = (item: InstrumentItem): ReactNode => (
    <input
      className="instr-field"
      value={item.group ?? ''}
      placeholder="Group label (optional)"
      aria-label="Group label"
      title="Optional heading shown above this question — consecutive questions given the same label sit together under it"
      onChange={(e) =>
        withSection((s) => {
          const target =
            s.scorecard.find((it) => it.id === item.id) ??
            s.reflection.find((q) => q.id === item.id);
          if (!target) return;
          const v = e.target.value;
          if (v) target.group = v;
          else delete target.group;
        })
      }
    />
  );

  const writtenCount = section.reflection.length;

  return (
    <div className="card">
      <h2
        className="health-section-heading"
        style={collapsed ? { border: 0, margin: 0, paddingBottom: 0 } : undefined}
      >
        <span
          style={{ display: 'inline-flex', gap: 8, alignItems: 'baseline', flex: 1, minWidth: 0 }}
        >
          <button
            type="button"
            className="instr-ctl"
            aria-label={collapsed ? 'Expand section' : 'Collapse section'}
            aria-expanded={!collapsed}
            data-tip={collapsed ? 'Show the questions in this section' : 'Collapse to one line'}
            onClick={onToggleCollapsed}
          >
            {collapsed ? '▸' : '▾'}
          </button>
          <span>{section.no}.</span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <InlineMarkupEditor
              value={section.title}
              onSave={(v) => withSection((s) => (s.title = v))}
              placeholder="Section title"
            />
          </span>
        </span>
        <span style={{ display: 'inline-flex', gap: 4, textTransform: 'none', letterSpacing: 0 }}>
          <button
            type="button"
            className="instr-ctl"
            aria-label="Move section up"
            data-tip="Move this whole section earlier in the assessment"
            disabled={sectionIndex === 0}
            onClick={() => update((d) => moveInArray(d.sections, sectionIndex, sectionIndex - 1))}
          >
            ↑
          </button>
          <button
            type="button"
            className="instr-ctl"
            aria-label="Move section down"
            data-tip="Move this whole section later in the assessment"
            disabled={sectionIndex === sectionCount - 1}
            onClick={() => update((d) => moveInArray(d.sections, sectionIndex, sectionIndex + 1))}
          >
            ↓
          </button>
          <button
            type="button"
            className="instr-ctl"
            data-tip="See this section exactly as respondents will"
            onClick={onTogglePreview}
          >
            {previewOpen ? 'Close preview' : 'Preview'}
          </button>
          <button
            type="button"
            className="instr-ctl instr-ctl-danger"
            aria-label="Delete section"
            data-tip="Delete this section and every question in it"
            onClick={deleteSection}
          >
            ✕
          </button>
        </span>
      </h2>

      {collapsed ? (
        <button type="button" className="instr-collapsed-summary" onClick={onToggleCollapsed}>
          {section.scorecard.length} scale{' '}
          {section.scorecard.length === 1 ? 'question' : 'questions'} · {writtenCount} written
          {section.designation ? ` · ${section.designation}` : ''}
        </button>
      ) : (
        <>
          <div style={{ display: 'grid', gap: 4, marginBottom: 14, marginTop: 14 }}>
            <span className="health-input-label">Aim</span>
            <InlineMarkupEditor
              value={section.aim}
              onSave={(v) => withSection((s) => (s.aim = v))}
              placeholder="What this section assesses"
            />
          </div>

          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap',
              marginBottom: 14,
            }}
          >
            <span className="health-input-label">Designation</span>
            <select
              className="instr-field"
              value={section.designation ?? ''}
              aria-label="Section designation"
              title="Applies to every scale question in this section; individual questions can override below. Safeguarding results are flagged when low; governance results are always shown but never alarm-flagged."
              onChange={(e) =>
                withSection((s) => {
                  const v = e.target.value;
                  if (v === 'safeguarding' || v === 'governance') s.designation = v;
                  else delete s.designation;
                })
              }
            >
              <option value="">None</option>
              <option value="safeguarding">Safeguarding — flagged when low</option>
              <option value="governance">Governance — shown, never flagged</option>
            </select>
            {section.designation && (
              <span style={{ fontSize: 12, color: 'var(--health-grey)' }}>
                Every scale question in this section, unless a question overrides it.
              </span>
            )}
          </div>

          <span className="health-input-label">Scale questions (scored 1 to 4)</span>
          <div style={{ marginBottom: 8 }}>
            {section.scorecard.map((item, i) => (
              <div key={item.id}>
                <div className="instr-item-row">
                  <div className="instr-item-main">
                    <InlineMarkupEditor
                      value={item.text}
                      onSave={(v) =>
                        withSection((s) => {
                          const it = s.scorecard.find((x) => x.id === item.id);
                          if (it) it.text = v;
                        })
                      }
                      placeholder="Write the statement to score..."
                    />
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                        marginTop: 2,
                        flexWrap: 'wrap',
                      }}
                    >
                      {groupInput(item)}
                      <select
                        className="instr-field"
                        value={item.designation ?? ''}
                        aria-label="Question designation"
                        title="Overrides the section's designation for this one question. Safeguarding results are flagged when low; governance results are shown but never alarm-flagged."
                        onChange={(e) =>
                          withSection((s) => {
                            const it = s.scorecard.find((x) => x.id === item.id);
                            if (!it) return;
                            const v = e.target.value;
                            if (v === 'safeguarding' || v === 'governance' || v === 'none') {
                              it.designation = v;
                            } else {
                              delete it.designation;
                            }
                          })
                        }
                      >
                        <option value="">
                          {section.designation
                            ? `Follows section (${section.designation})`
                            : 'Follows section (none)'}
                        </option>
                        <option value="safeguarding">Safeguarding — flagged when low</option>
                        <option value="governance">Governance — shown, never flagged</option>
                        <option value="none">Not designated</option>
                      </select>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <InlineMarkupEditor
                          className="instr-edit-note"
                          value={item.note ?? ''}
                          onSave={(v) =>
                            withSection((s) => {
                              const it = s.scorecard.find((x) => x.id === item.id);
                              if (!it) return;
                              if (v) it.note = v;
                              else delete it.note;
                            })
                          }
                          placeholder="Note shown under the statement (optional)"
                        />
                      </div>
                    </div>
                  </div>
                  {itemControls(item, 'scorecard', i, section.scorecard.length)}
                </div>
                {followUpRows(item)}
              </div>
            ))}
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            data-tip="Add a statement respondents score from 1 to 4"
            onClick={() =>
              withSection((s) => {
                s.scorecard.push({ id: newInstrumentId('c'), text: '' });
              })
            }
          >
            + Add a scale question
          </button>

          <div style={{ marginTop: 18 }}>
            <span className="health-input-label">Reflection questions (written answers)</span>
            <div style={{ marginBottom: 8 }}>
              {topReflection.map((qn, i) => (
                <div key={qn.id}>
                  <div className="instr-item-row">
                    <div className="instr-item-main">
                      <InlineMarkupEditor
                        value={qn.text}
                        onSave={(v) =>
                          withSection((s) => {
                            const q = s.reflection.find((r) => r.id === qn.id);
                            if (q) q.text = v;
                          })
                        }
                        placeholder="Write the reflection question..."
                      />
                      <div style={{ marginTop: 2 }}>{groupInput(qn)}</div>
                    </div>
                    {itemControls(qn, 'top', i, topReflection.length)}
                  </div>
                  {followUpRows(qn)}
                </div>
              ))}
            </div>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              data-tip="Add a question answered in writing rather than scored"
              onClick={() =>
                withSection((s) => {
                  s.reflection.push({ id: newInstrumentId('q'), text: '' });
                })
              }
            >
              + Add a reflection question
            </button>
          </div>

          {section.scorecard.length === 0 && topReflection.length === 0 && (
            <p style={{ fontSize: 12.5, color: 'var(--health-grey)', marginTop: 10 }}>
              This section has no questions yet, so it will not appear to respondents until it does.
            </p>
          )}
        </>
      )}
    </div>
  );
}
